import type { ReviewCourse } from "@crm/db/schema";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Option } from "effect";
import { AsyncResult } from "effect/unstable/reactivity";
import { BookOpen, MoreHorizontal, Search } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import {
  useCourseProgress,
  useReviewCourses,
  useReviewLessonsByCourse,
  useReviewModulesByCourse,
} from "#app/review-courses";
import { CourseCard } from "./-components/course-card";
import { useCoursesProgress } from "./-hooks/use-courses-progress";
import { CourseCompletionCelebration } from "./$courseId/-components/course-completion-celebration";

/**
 * 课程复习页面
 */
export const Route = createFileRoute("/_protected/review-course/")({
  head: () => ({
  }),
  component: RouteComponent,
});

// --- Main Page Component ---

function RouteComponent() {
  const [searchQuery, setSearchQuery] = useState("");
  // 显示值（用户始终能看到输入，包括拼音）
  const [displayValue, setDisplayValue] = useState("");
  const [learningFilter, setLearningFilter] = useState<
    "all" | "completed" | "in-progress" | "not-started"
  >("all");
  const [selectedCourseForCelebration, setSelectedCourseForCelebration] =
    useState<ReviewCourse | null>(null);

  // IME 输入处理（中文输入法支持）
  const isComposingRef = useRef(false);

  // Fetch review courses from database (only published courses for students)
  const coursesResult = useReviewCourses({
    status: "published",
  });
  const courses = AsyncResult.value(coursesResult).pipe(Option.getOrUndefined) ?? [];
  const isLoading = AsyncResult.isWaiting(coursesResult);

  const courseIds = useMemo(() => courses.map((course) => course.id), [courses]);

  // Get progress for all courses
  const coursesProgress = useCoursesProgress(courseIds);

  // Filter courses based on search and learning status
  const filteredCourses = useMemo(
    () =>
      courses.filter((course: ReviewCourse) => {
        const matchesSearch =
          searchQuery === "" ||
          course.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          course.description?.toLowerCase().includes(searchQuery.toLowerCase());

        const progress = coursesProgress.get(course.id);
        const completionPercentage = progress?.completionPercentage ?? 0;
        const isCompleted = progress?.isFullyCompleted ?? false;

        let matchesLearningStatus = true;
        if (learningFilter === "completed") {
          matchesLearningStatus = isCompleted;
        } else if (learningFilter === "in-progress") {
          matchesLearningStatus = completionPercentage > 0 && !isCompleted;
        } else if (learningFilter === "not-started") {
          matchesLearningStatus = completionPercentage === 0;
        }

        return matchesSearch && matchesLearningStatus;
      }),
    [courses, searchQuery, learningFilter, coursesProgress],
  );

  // Calculate student-focused stats
  const completedCount = Array.from(coursesProgress.values()).filter(
    (p) => p.isFullyCompleted,
  ).length;
  const inProgressCount = Array.from(coursesProgress.values()).filter(
    (p) => p.completionPercentage > 0 && !p.isFullyCompleted,
  ).length;

  return (
    <>
      {/* Celebration Modal */}
      {selectedCourseForCelebration && (
        <CelebrationModalWrapper
          course={selectedCourseForCelebration}
          onClose={() => setSelectedCourseForCelebration(null)}
        />
      )}

      <div className="min-h-screen bg-muted pb-24 text-ink selection:bg-primary selection:text-ink">
        <main className="mx-auto max-w-[1280px] px-4 pt-12 md:px-8">
          {/* Header Section */}
          <section className="mb-16 space-y-6">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
              <div>
                <h2 className="mb-2 font-bold font-mono text-primary text-sm uppercase tracking-widest">
                  Dashboard / Student
                </h2>
                <h1 className="font-bold font-mono text-4xl text-ink uppercase tracking-tight md:text-6xl">
                  Course Review
                  <span className="mt-2 block font-bold font-sans text-2xl text-ink/80 md:text-3xl">
                    课程复习与进度管理
                  </span>
                </h1>
              </div>

              {/* 学习统计 - 学员视角 */}
              <div className="flex gap-[-2px]">
                <div className="w-32 border border-border bg-white p-4 text-center">
                  <div className="mb-1 font-mono text-gray-500 text-xs uppercase">学习中</div>
                  <div className="font-bold font-mono text-3xl">{inProgressCount}</div>
                </div>
                <div className="w-32 border border-border border-l-0 bg-success p-4 text-center">
                  <div className="mb-1 font-mono text-white text-xs uppercase">已完成</div>
                  <div className="font-bold font-mono text-3xl text-white">{completedCount}</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 md:flex-row md:items-center">
              <p className="max-w-2xl font-sans text-ink/80 text-lg leading-relaxed">
                欢迎回来。这里汇集了你所有的学习轨迹。保持理性的节奏，继续探索知识的边界。
              </p>
              <Link
                className="inline-flex items-center gap-2 whitespace-nowrap border border-border bg-white px-4 py-2 font-bold font-mono text-sm transition-all hover:border-primary hover:bg-primary hover:text-white"
                to="/knowledge"
              >
                <BookOpen size={16} />
                知识库
              </Link>
            </div>
          </section>

          <hr className="mb-12 border-border border-t-2" />

          {/* Toolbar Section */}
          <section className="mb-10 flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center">
            <div className="group relative w-full md:w-96">
              <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                <Search className="text-gray-400 group-focus-within:text-primary" size={20} />
              </div>
              <input
                className="w-full rounded-md border border-border bg-white py-3 pr-4 pl-10 font-mono outline-none transition-all placeholder:font-sans placeholder:text-gray-400 focus:border-primary focus:shadow-s"
                onChange={(e) => {
                  const value = e.target.value;
                  // 始终更新显示值（用户能看到拼音）
                  setDisplayValue(value);
                  // 只在非 composing 时触发搜索
                  if (!isComposingRef.current) {
                    setSearchQuery(value);
                  }
                }}
                onCompositionEnd={(e) => {
                  isComposingRef.current = false;
                  setSearchQuery(e.currentTarget.value);
                }}
                onCompositionStart={() => {
                  isComposingRef.current = true;
                }}
                placeholder="搜索课程名称或关键词..."
                type="text"
                value={displayValue}
              />
            </div>

            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
              {/* Learning Status Filters */}
              {[
                { label: "全部课程", value: "all" as const },
                { label: "已完成", value: "completed" as const },
                { label: "学习中", value: "in-progress" as const },
                { label: "未开始", value: "not-started" as const },
              ].map((filter) => (
                <button
                  className={`whitespace-nowrap rounded-md border border-border px-4 py-2 font-bold font-mono text-sm transition-all ${learningFilter === filter.value ? "bg-ink text-white" : "bg-white hover:bg-muted"}
                    `}
                  key={filter.value}
                  onClick={() => setLearningFilter(filter.value)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </section>

          {/* Course Grid */}
          <section className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {isLoading ? (
              <div className="col-span-full py-12 text-center">
                <div className="font-mono text-gray-500">加载中...</div>
              </div>
            ) : filteredCourses.length === 0 ? (
              <div className="col-span-full py-12 text-center">
                <div className="font-mono text-gray-500">
                  {searchQuery || learningFilter !== "all"
                    ? "没有找到匹配的课程"
                    : "暂无可学习的课程"}
                </div>
              </div>
            ) : (
              filteredCourses.map((course: ReviewCourse) => {
                const progress = coursesProgress.get(course.id);
                return (
                  <CourseCard
                    completionPercentage={progress?.completionPercentage ?? 0}
                    course={course}
                    isFullyCompleted={progress?.isFullyCompleted ?? false}
                    key={course.id}
                    onCelebrationClick={() => setSelectedCourseForCelebration(course)}
                  />
                );
              })
            )}

            {/* Empty State / Add New Placeholder (Optional playful element) */}
            {!isLoading && (
              <div className="group flex min-h-[300px] cursor-pointer flex-col items-center justify-center rounded-md border border-ink/40 border-dashed p-8 transition-all hover:border-primary hover:bg-white">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted transition-colors group-hover:bg-primary">
                  <MoreHorizontal className="text-ink" size={32} />
                </div>
                <h3 className="font-bold font-mono text-ink/60 text-lg group-hover:text-ink">
                  探索新课程
                </h3>
                <p className="mt-2 max-w-[200px] text-center text-gray-500 text-sm">
                  还有更多未知领域等待解锁
                </p>
              </div>
            )}
          </section>

          {/* Footer Text */}
          <div className="mt-24 text-center font-mono text-ink/40 text-xs uppercase tracking-widest">
            Motherduck Design System · Visible Order
          </div>
        </main>
      </div>
    </>
  );
}

// Wrapper component to fetch course data for celebration modal
function CelebrationModalWrapper({
  course,
  onClose,
}: {
  course: ReviewCourse;
  onClose: () => void;
}) {
  // Fetch lessons and modules for this course
  const { allCourseLessons } = useCourseData(course.id);

  // Get completion data for this course (server-side filtered)
  const courseProgressResult = useCourseProgress(course.id);
  const courseProgress =
    AsyncResult.value(courseProgressResult).pipe(Option.getOrUndefined) ?? [];

  const completedLessons = useMemo(
    () => courseProgress.filter((p) => p.completed),
    [courseProgress],
  );

  return (
    <CourseCompletionCelebration
      allLessons={allCourseLessons}
      completedLessons={completedLessons}
      course={course}
      onClose={onClose}
    />
  );
}

// Simplified useCourseData hook for the celebration modal
function useCourseData(courseId: string) {
  const lessonsResult = useReviewLessonsByCourse(courseId);
  const modulesResult = useReviewModulesByCourse(courseId);
  const allCourseLessons =
    AsyncResult.value(lessonsResult).pipe(Option.getOrUndefined) ?? [];
  const allModules = AsyncResult.value(modulesResult).pipe(Option.getOrUndefined) ?? [];

  const moduleIds = useMemo(() => new Set(allModules.map((m) => m.id)), [allModules]);
  const sortedLessons = useMemo(
    () =>
      allCourseLessons
        .filter((lesson) => moduleIds.has(lesson.moduleId))
        .sort((a, b) => a.order - b.order),
    [allCourseLessons, moduleIds],
  );

  return {
    allCourseLessons: sortedLessons,
  };
}
